import { Account } from "../../api/dealer/DealerApi";
import { RBACRole, RBACRolesResponse } from "../../api/rbac/types";
import {
  FeatureToggleDetail,
  SelectedValue,
  ToggleLevel,
} from "../../pages/featureToggles/types";

export type RBACDetails = {
  accounts: Account[] | undefined;
  featureToggles: FeatureToggleDetail[] | undefined;
};

export const isRBACEnabledForDealerFacingRole = (
  rbacDetails: RBACDetails
): { toggleProcessed: boolean; rbacEnabled: boolean } => {
  const { accounts, featureToggles } = rbacDetails;
  const accountIds: string[] = getAccountIds(accounts);
  const uniqueCountries: string[] = getUniqueCountries(accounts);
  const mruList: string[] = getMruIds(accounts);
  const rbacFeatureToggles: FeatureToggleDetail[] =
    getRbacFeatureToggle(featureToggles);
  const selectedValues: SelectedValue[] =
    rbacFeatureToggles[0]?.selectedValues ?? [];
  if (selectedValues !== undefined) {
    const rbacResults: boolean[] = rbacResult(
      selectedValues,
      accountIds,
      uniqueCountries,
      mruList
    );
    return {
      toggleProcessed: true,
      rbacEnabled: isRbacEnabledForDealer(rbacResults),
    };
  }
  return { toggleProcessed: true, rbacEnabled: false };
};

const rbacResult = (
  selectedValues: SelectedValue[],
  accountIds: string[],
  uniqueCountries: string[],
  mruList: string[]
): boolean[] => {
  const rbacResultList: boolean[] = [];
  for (let i = 0; i < selectedValues.length; i += 1) {
    const {
      enabledFor,
      disabledFor,
      country,
      level: toggleLevel,
    } = selectedValues[i];
    const result = findRBACEnabled(
      toggleLevel,
      disabledFor,
      accountIds,
      enabledFor,
      uniqueCountries,
      country ?? "",
      mruList
    );
    rbacResultList.push(result);
  }
  return rbacResultList;
};

const isRbacEnabledForDealer = (rbacResultList: boolean[]): boolean =>
  rbacResultList.some((result) => result);

const getRbacFeatureToggle = (
  featureToggles: FeatureToggleDetail[] | undefined
): FeatureToggleDetail[] =>
  featureToggles !== undefined
    ? featureToggles.filter(
        (featureToggle) =>
          featureToggle.categoryKey === "rbac-service" &&
          featureToggle.key === "enable_rbac_integration"
      )
    : [];

export const isJDUser = (
  userRoleDetails: RBACRolesResponse | undefined
): boolean => checkIfUserHasJDRole(userRoleDetails?.roles ?? []);

const getAccountIds = (accounts: Account[] | undefined): string[] => {
  const accountList = accounts ?? [];
  return accountList.map((account) => account.id);
};

const getUniqueCountries = (accounts: Account[] | undefined): string[] => {
  const accountDetailList = accounts ?? [];
  const countries = accountDetailList.map(
    (accDetail) => accDetail.location?.country ?? ""
  );
  return Array.from(new Set(countries));
};

const getMruIds = (accounts: Account[] | undefined): string[] => {
  const accountDetailList = accounts ?? [];
  return Array.from(
    new Set(accountDetailList.map((accountDetail) => accountDetail.mruSalesOrg))
  );
};

const isAnyElementInList = (elements: string[], list: string[]): boolean =>
  elements.some((element) => list.includes(element));

const findRBACEnabled = (
  toggleLevel: string,
  disabledFor: string[],
  accountsIds: string[],
  enabledFor: string[],
  uniqueCountries: (string | undefined)[],
  country: string,
  mruList: string[]
) => {
  const checkDisabledOrEnabled = (elements: string[]): boolean => {
    if (disabledFor.length > 0) {
      return !isAnyElementInList(elements, disabledFor);
    }
    if (enabledFor.length > 0) {
      return isAnyElementInList(elements, enabledFor);
    }
    return false;
  };

  switch (toggleLevel) {
    case ToggleLevel.ACCOUNT:
      return checkDisabledOrEnabled(accountsIds);
    case ToggleLevel.COUNTRY:
      return uniqueCountries.includes(country);
    case ToggleLevel.BRANCH:
      return checkDisabledOrEnabled(mruList);
    default:
      return false;
  }
};

const checkIfUserHasJDRole = (roles: RBACRole[]): boolean =>
  roles.some(
    (role) =>
      role === RBACRole.JD_ADMIN ||
      role === RBACRole.JD_SUPER_ADMIN ||
      role === RBACRole.JD_READ_ONLY ||
      role === RBACRole.JD_DEVELOPER_ADMIN
  );
